/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ExplainedAccessDirectorySource,
  ExplainedAccessDirectorySource$inboundSchema,
} from "./explainedaccessdirectorysource.js";

/**
 * Whether the rule names this resource, every resource in its project, or every resource of its kind.
 */
export const AppliesTo = {
  Resource: "resource",
  Project: "project",
  AllResources: "all_resources",
} as const;
/**
 * Whether the rule names this resource, every resource in its project, or every resource of its kind.
 */
export type AppliesTo = ClosedEnum<typeof AppliesTo>;

export const Dispositions = {
  ReadOnly: "read_only",
  Destructive: "destructive",
  Idempotent: "idempotent",
  OpenWorld: "open_world",
} as const;
export type Dispositions = ClosedEnum<typeof Dispositions>;

/**
 * How the rule shaped the decision. allows: proves the access. overrides: a rule made directly to the member that proves the access despite a block from a role or everyone. overridden: a block such a rule overrides. blocks: takes the access away. blocked: an allow that matches but that a block keeps from counting. limits: a block taking away some of the resource's tools.
 */
export const Effect = {
  Allows: "allows",
  Overrides: "overrides",
  Overridden: "overridden",
  Blocks: "blocks",
  Blocked: "blocked",
  Limits: "limits",
} as const;
/**
 * How the rule shaped the decision. allows: proves the access. overrides: a rule made directly to the member that proves the access despite a block from a role or everyone. overridden: a block such a rule overrides. blocks: takes the access away. blocked: an allow that matches but that a block keeps from counting. limits: a block taking away some of the resource's tools.
 */
export type Effect = ClosedEnum<typeof Effect>;

/**
 * What the principal identifies.
 */
export const Kind = {
  Everyone: "everyone",
  Role: "role",
  User: "user",
  Unknown: "unknown",
} as const;
/**
 * What the principal identifies.
 */
export type Kind = ClosedEnum<typeof Kind>;

/**
 * The access the rule gives, or the access a "blocked_" rule takes away. "all" is a grant covering every permission.
 */
export const ExplainedAccessRuleLevel = {
  Use: "use",
  View: "view",
  Manage: "manage",
  Blocked: "blocked",
  BlockedView: "blocked_view",
  BlockedManage: "blocked_manage",
  All: "all",
} as const;
/**
 * The access the rule gives, or the access a "blocked_" rule takes away. "all" is a grant covering every permission.
 */
export type ExplainedAccessRuleLevel = ClosedEnum<
  typeof ExplainedAccessRuleLevel
>;

/**
 * Why a blocked rule made directly to the member did not override the block. wildcard_direct_grant: it covers every resource. narrower_direct_grant: it constrains something the tool does not carry. own_exclusion: the member's own block applies.
 */
export const Reason = {
  WildcardDirectGrant: "wildcard_direct_grant",
  NarrowerDirectGrant: "narrower_direct_grant",
  OwnExclusion: "own_exclusion",
} as const;
/**
 * Why a blocked rule made directly to the member did not override the block. wildcard_direct_grant: it covers every resource. narrower_direct_grant: it constrains something the tool does not carry. own_exclusion: the member's own block applies.
 */
export type Reason = ClosedEnum<typeof Reason>;

export type ExplainedAccessRule = {
  /**
   * Whether the rule names this resource, every resource in its project, or every resource of its kind.
   */
  appliesTo: AppliesTo;
  /**
   * The directory role mappings giving the member this role, including when they also hold it directly. Returned only to organization administrators, because attribute values can carry personal data.
   */
  directorySources?: Array<ExplainedAccessDirectorySource> | undefined;
  /**
   * Human-readable name for the principal.
   */
  displayName: string;
  /**
   * Tool annotations the rule is narrowed to, when it is not the whole resource.
   */
  dispositions?: Array<Dispositions> | undefined;
  /**
   * How the rule shaped the decision. allows: proves the access. overrides: a rule made directly to the member that proves the access despite a block from a role or everyone. overridden: a block such a rule overrides. blocks: takes the access away. blocked: an allow that matches but that a block keeps from counting. limits: a block taking away some of the resource's tools.
   */
  effect: Effect;
  /**
   * What the principal identifies.
   */
  kind: Kind;
  /**
   * The access the rule gives, or the access a "blocked_" rule takes away. "all" is a grant covering every permission.
   */
  level: ExplainedAccessRuleLevel;
  /**
   * Canonical principal URN holding the rule.
   */
  principalUrn: string;
  /**
   * Why a blocked rule made directly to the member did not override the block. wildcard_direct_grant: it covers every resource. narrower_direct_grant: it constrains something the tool does not carry. own_exclusion: the member's own block applies.
   */
  reason?: Reason | undefined;
  /**
   * Tool names the rule is narrowed to, when it is not the whole resource.
   */
  tools?: Array<string> | undefined;
  /**
   * Whether the member holds this role only through a directory role mapping.
   */
  viaDirectoryMapping: boolean;
};

/** @internal */
export const AppliesTo$inboundSchema: z.ZodMiniEnum<typeof AppliesTo> = z.enum(
  AppliesTo,
);

/** @internal */
export const Dispositions$inboundSchema: z.ZodMiniEnum<typeof Dispositions> = z
  .enum(Dispositions);

/** @internal */
export const Effect$inboundSchema: z.ZodMiniEnum<typeof Effect> = z.enum(
  Effect,
);

/** @internal */
export const Kind$inboundSchema: z.ZodMiniEnum<typeof Kind> = z.enum(Kind);

/** @internal */
export const ExplainedAccessRuleLevel$inboundSchema: z.ZodMiniEnum<
  typeof ExplainedAccessRuleLevel
> = z.enum(ExplainedAccessRuleLevel);

/** @internal */
export const Reason$inboundSchema: z.ZodMiniEnum<typeof Reason> = z.enum(
  Reason,
);

/** @internal */
export const ExplainedAccessRule$inboundSchema: z.ZodMiniType<
  ExplainedAccessRule,
  unknown
> = z.pipe(
  z.object({
    applies_to: AppliesTo$inboundSchema,
    directory_sources: z.optional(
      z.array(ExplainedAccessDirectorySource$inboundSchema),
    ),
    display_name: z.string(),
    dispositions: z.optional(z.array(Dispositions$inboundSchema)),
    effect: Effect$inboundSchema,
    kind: Kind$inboundSchema,
    level: ExplainedAccessRuleLevel$inboundSchema,
    principal_urn: z.string(),
    reason: z.optional(Reason$inboundSchema),
    tools: z.optional(z.array(z.string())),
    via_directory_mapping: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "applies_to": "appliesTo",
      "directory_sources": "directorySources",
      "display_name": "displayName",
      "principal_urn": "principalUrn",
      "via_directory_mapping": "viaDirectoryMapping",
    });
  }),
);

export function explainedAccessRuleFromJSON(
  jsonString: string,
): SafeParseResult<ExplainedAccessRule, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ExplainedAccessRule$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ExplainedAccessRule' from JSON`,
  );
}
